/**
 * Unsupported-device AI Assist handoff.
 *
 * When the guided activation flow reaches the "device not supported" step, there is no guide path for
 * the customer's device. Instead of a dead end, we surface an "Ask Assist" CTA that hands the expert
 * off to AI Assist with the device + activation context we already know (see `GuideCard`).
 *
 * The step opts in via Simplr's `data.showAskAssist` boolean — the FE no longer matches the step's
 * `stepId` against a hardcoded list, so adding the CTA to a new step is a flow-authoring change only.
 */

export interface UnsupportedDeviceAssistContext {
    make?: string;
    model?: string;
    /** From `verifyCustomer` — "REPLACEMENT_DEVICE" | "NEW_DEVICE". */
    activationFlowType?: string;
    /** A Simplr-authored `assistPrompt` for the step, if set — it takes precedence over a generated one. */
    authoredPrompt?: string;
}

/** How the prompt's device context was resolved — reported to analytics for the demand signal. */
export type UnsupportedDeviceAssistPromptType = "authored" | "device" | "inferred";

export interface UnsupportedDeviceAssist {
    /** The (verbose) question sent to AI Assist — hidden from the expert behind an "Activation Assist" chip. */
    prompt: string;
    promptType: UnsupportedDeviceAssistPromptType;
}

const ACTIVATION_FLOW_LABEL: Record<string, string> = {
    NEW_DEVICE: "new device",
    REPLACEMENT_DEVICE: "replacement device",
};

/**
 * Build the AI Assist handoff for the unsupported-device step. Always returns a prompt (the guided flow
 * has no path for this device, so we always hand off rather than dead-end) together with the `promptType`
 * that produced it, so callers report accurate analytics without recomputing it:
 *
 * - `authored`: a Simplr-authored `assistPrompt` was supplied (takes precedence).
 * - `device`: we know the device (make/model) and ask for it specifically.
 * - `inferred`: we don't know the device, so we ask AI Assist to infer it from the call transcript.
 *
 * The verbose text is never shown to the expert — it rides behind a short "Activation Assist" chip via
 * `helpMeNowText` (see `UnsupportedDeviceAssistButton`), so it can carry as much context as we want.
 */
export function buildUnsupportedDeviceAssistPrompt({
    make,
    model,
    activationFlowType,
    authoredPrompt,
}: UnsupportedDeviceAssistContext): UnsupportedDeviceAssist {
    if (authoredPrompt) {
        return { prompt: authoredPrompt, promptType: "authored" };
    }

    const device = [make, model].filter(Boolean).join(" ").trim();
    const flowLabel = activationFlowType ? ACTIVATION_FLOW_LABEL[activationFlowType] : undefined;
    const flowClause = flowLabel ? ` This is a ${flowLabel} activation.` : "";

    if (device) {
        return {
            prompt: `A customer needs to activate a ${device}, which our guided activation flow doesn't support.${flowClause} Walk me through the steps to activate this device.`,
            promptType: "device",
        };
    }

    return {
        prompt: `A customer needs to activate a device that our guided activation flow doesn't support.${flowClause} Based on the call so far, identify which device the customer is activating and walk me through the steps to activate it. If the device isn't clear yet, tell me what to ask the customer.`,
        promptType: "inferred",
    };
}
